import React, { useState, useEffect } from 'react'
import { useLocation, Link } from 'react-router-dom'
import {
  Avatar,
  Breadcrumbs,
  Button,
  Grid,
  Heading,
  Input,
  Layout,
  Menu,
  Tooltip,
} from '@design-systems/canvas-ui'
import {
  useAnalytics,
  useAuth,
  usePageInfo,
  useLocalStorage,
} from 'properties-ui-common'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSignOutAlt,
  faAngleDown,
  faCommentMedical,
} from '@fortawesome/free-solid-svg-icons'
import { AppRoutes } from './AppRoutes/AppRoutes'
import { AutoBreadcrumbManager } from '../../AutoBreadcrumbManager/AutoBreadcrumbManager'
import { UserInfo } from './utilities/UserInfo'
import { FeedbackWidget } from './MenuBar/FeedbackWidget'
import { SideNavigation } from './SideNavigation/Sidenavigation'
import { getPageLabel } from './utilities/getPageLabel'
import logo from '../../images/mosaic.png'
import './MainWrapper.css'
import { MfeDevManager } from '../../MfeDevManager'
import { apiConfig } from '../../../apiConfig'

export const MainWrapper = () => {
  const { pathname } = useLocation()
  const [localStorageThemeValue, setLocalStorageThemeValue] = useLocalStorage(
    'skylight.darkMode',
    false,
  )
  const [darkMode, setDarkMode] = useState<boolean>(
    window.matchMedia('(prefers-color-scheme: dark)').matches ||
      localStorageThemeValue,
  )
  const [railOpen, setRailOpen] = useState(false)

  const { breadcrumbs } = usePageInfo()
  const { sendEvent } = useAnalytics()
  const auth = useAuth()

  const isLocalhost =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  const [mfeDevManagerVisible, setMfeDevManagerVisible] = React.useState(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem('skylight.mfeDevManagerVisible')
    return saved ? JSON.parse(saved) : false
  })

  // Persist to localStorage when changed
  React.useEffect(() => {
    localStorage.setItem(
      'skylight.mfeDevManagerVisible',
      JSON.stringify(mfeDevManagerVisible),
    )
  }, [mfeDevManagerVisible])

  const lanId = auth?.session?.userInfo?.lanId ?? ''
  useEffect(() => {
    if (lanId) {
      sendEvent({
        event: { type: 'LOGIN', id: lanId },
        //@ts-ignore
        client: {
          employee_login_id: lanId,
        },
      })
    }
  }, [sendEvent, lanId])

  useEffect(() => {
    setLocalStorageThemeValue(darkMode)
  }, [darkMode, setLocalStorageThemeValue])

  useEffect(() => {
    const { page } = getPageLabel(pathname)
    sendEvent({
      event: {
        type: 'PAGE_VIEW',
        value: page,
        id: pathname,
      },
      //@ts-ignore
      client: {
        employee_login_id: lanId,
      },
    })
  }, [pathname, sendEvent, lanId])

  const LogoSlot: React.FC = () => (
    <Link
      to="/"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        textDecoration: 'none',
      }}
    >
      <img src={logo} alt="Skylight" height={32} width={32} className="logo" />
      <Heading size={4}>
        {/* {heading && heading.length > 0 ? heading : 'Skylight'} */}
        Skylight
      </Heading>
    </Link>
  )

  const ActionsSlot: React.FC = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {isLocalhost && (
        <Input.Switch
          style={{ whiteSpace: 'nowrap' }}
          hasIcon
          className="hc8-mr-3x"
          id="mfe_dev_manager_switch"
          label="MFE Dev Manager"
          isChecked={mfeDevManagerVisible}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setMfeDevManagerVisible(event.target.checked)
          }
        />
      )}
      <Input.Switch
        hasIcon
        id="dark_mode_switch"
        label="Dark Mode"
        className="hc8-mr-3x"
        isChecked={darkMode}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setDarkMode(event.target.checked)
        }
      />
      <Tooltip content="Provide feedback" location="bottom">
        <Button
          variant="default"
          isIconOnly
          className="hc8-mr-3x"
          size="md"
          aria-label="Provide feedback"
          onClick={() => setRailOpen(true)}
        >
          <FontAwesomeIcon icon={faCommentMedical as any} size="lg" />
        </Button>
      </Tooltip>
    </div>
  )

  const ProfileSlot: React.FC = () => (
    <Menu location="bottom-right">
      <Menu.Toggle aria-label="User menu">
        <Grid.Container
          spacing="none"
          align="center"
          className="avatarContainer"
        >
          <Grid.Item>
            <Avatar size="sm" aria-label="User initials" color="blue">
              <UserInfo nameType="initials" />
            </Avatar>
          </Grid.Item>
          <Grid.Item>
            <FontAwesomeIcon
              className="hc-ml-dense"
              color="lightGrey"
              icon={faAngleDown as any}
              size="lg"
            />
          </Grid.Item>
        </Grid.Container>
      </Menu.Toggle>
      <Menu.Item isDisabled className="hc-clr-black loggedInAs">
        Logged in as <UserInfo nameType="full" />
      </Menu.Item>
      <Menu.Item
        onClick={() => {
          auth.logout()
          sessionStorage.clear()
        }}
      >
        Logout{' '}
        <FontAwesomeIcon icon={faSignOutAlt as any} className="hc-ml-dense" />
      </Menu.Item>
    </Menu>
  )

  return (
    <Layout
      darkMode={darkMode}
      className={`${darkMode ? 'C-Mode--dark' : 'C-Mode--light'} C-Layout C-Theme--default`}
    >
      <Layout.GlobalHeader
        logoSlot={<LogoSlot />}
        // hamburgerSlot={<HamburgerSlot />}
        actionsSlot={<ActionsSlot />}
        profileSlot={<ProfileSlot />}
      />
      <SideNavigation />
      {breadcrumbs && Array.isArray(breadcrumbs) && breadcrumbs.length > 0 && (
        <Layout.PageHeader>
          <Breadcrumbs>
            {breadcrumbs
              .filter((crumb: any) => crumb && typeof crumb.name === 'string')
              .map((crumb: any) => (
                <Breadcrumbs.Item
                  key={crumb.name}
                  href={crumb.href}
                  onClick={crumb.onClick}
                  data-testid={`${crumb.name}-breadcrumb`}
                >
                  {crumb.name}
                </Breadcrumbs.Item>
              ))}
          </Breadcrumbs>
        </Layout.PageHeader>
      )}
      <Layout.Body className="hc8-pa-3x">
        <AutoBreadcrumbManager>
          <AppRoutes />
        </AutoBreadcrumbManager>
      </Layout.Body>
      <FeedbackWidget isOpen={railOpen} onFeedback={() => setRailOpen(false)} />
      <MfeDevManager
        isVisible={mfeDevManagerVisible}
        microFrontEnds={apiConfig.microFrontEnds}
      />
    </Layout>
  )
}

export default MainWrapper
